import { apiFetch } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Camera, ArrowLeft, ChevronDown, LogOut, Loader2, Users,
  Languages, Bookmark, BarChart3, Clock, Mail, Globe
} from "lucide-react";


export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, usersRes, transRes] = await Promise.all([
        apiFetch(`/api/admin/stats`, {  }),
        apiFetch(`/api/admin/users`, {  }),
        apiFetch(`/api/admin/translations`, {  }),
      ]);

      if (statsRes.status === 403 || usersRes.status === 403) {
        setError("You don't have admin access.");
        setLoading(false);
        return;
      }

      const [statsData, usersData, transData] = await Promise.all([
        statsRes.json(), usersRes.json(), transRes.json(),
      ]);

      setStats(statsData);
      setUsers(usersData);
      setTranslations(transData);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "translations", label: "Translations", icon: Languages },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate("/dashboard")} className="bg-indigo-600 text-white rounded-full px-6">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>LingoLens</span>
            <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full ml-2">Admin</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full flex items-center gap-2 hover:bg-slate-50 px-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuItem className="text-sm text-slate-500 cursor-default">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="rounded-full hover:bg-indigo-50" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Admin Dashboard
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 mb-8 w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`admin-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-white text-indigo-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === "overview" && stats && (
              <div className="space-y-6 animate-fade-in">
                {/* Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-400">Total Users</span>
                    </div>
                    <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="stat-users">
                      {stats.user_count}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                        <Languages className="w-5 h-5 text-yellow-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-400">Translations</span>
                    </div>
                    <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="stat-translations">
                      {stats.translation_count}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                        <Bookmark className="w-5 h-5 text-pink-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-400">Bookmarked</span>
                    </div>
                    <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="stat-bookmarks">
                      {stats.bookmark_count}
                    </p>
                  </div>
                </div>

                {/* Language Breakdown */}
                {stats.languages?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                    <h3 className="text-lg font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      <Globe className="w-5 h-5 inline-block mr-2 text-indigo-500" />
                      Popular Languages
                    </h3>
                    <div className="space-y-3">
                      {stats.languages.map((l, i) => {
                        const max = stats.languages[0]?.count || 1;
                        const pct = Math.round((l.count / max) * 100);
                        return (
                          <div key={i} className="flex items-center gap-4">
                            <span className="text-sm font-semibold text-slate-700 w-36 truncate">{l.language}</span>
                            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-slate-500 w-8 text-right">{l.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Daily Translations */}
                {stats.daily_translations?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                    <h3 className="text-lg font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      <BarChart3 className="w-5 h-5 inline-block mr-2 text-yellow-500" />
                      Last 7 Days Activity
                    </h3>
                    <div className="flex items-end gap-2 h-32">
                      {stats.daily_translations.map((d, i) => {
                        const max = Math.max(...stats.daily_translations.map((x) => x.count), 1);
                        const h = Math.max((d.count / max) * 100, 8);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-xs font-bold text-slate-500">{d.count}</span>
                            <div
                              className="w-full bg-indigo-500 rounded-t-lg transition-all duration-500"
                              style={{ height: `${h}%` }}
                            />
                            <span className="text-[10px] text-slate-400 font-medium">
                              {d.date?.slice(5)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && users && (
              <div className="space-y-6 animate-fade-in">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6">
                  <h3 className="text-lg font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Google Users ({users.real_users?.length || 0})
                  </h3>
                  {users.real_users?.length === 0 ? (
                    <p className="text-slate-400 text-sm">No real users yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left py-3 px-3 font-bold text-slate-400 text-xs uppercase tracking-wider">User</th>
                            <th className="text-left py-3 px-3 font-bold text-slate-400 text-xs uppercase tracking-wider">Email</th>
                            <th className="text-left py-3 px-3 font-bold text-slate-400 text-xs uppercase tracking-wider">Translations</th>
                            <th className="text-left py-3 px-3 font-bold text-slate-400 text-xs uppercase tracking-wider">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.real_users.map((u, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-3">
                                  <Avatar className="w-8 h-8">
                                    <AvatarImage src={u.picture} />
                                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-bold">
                                      {u.name?.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="font-semibold text-slate-900">{u.name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-3">
                                <span className="text-slate-500 flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {u.email}
                                </span>
                              </td>
                              <td className="py-3 px-3">
                                <span className="inline-flex items-center bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold">
                                  {u.translation_count}
                                </span>
                              </td>
                              <td className="py-3 px-3">
                                <span className="text-slate-400 flex items-center gap-1 text-xs">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(u.created_at)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Translations Tab */}
            {activeTab === "translations" && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-slate-400 font-medium">Showing latest 50 translations across all users</p>
                {translations.map((t, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-5 hover:border-indigo-200 transition-all"
                    data-testid={`admin-translation-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {t.user_name} ({t.user_email})
                      </span>
                      <span className="text-xs font-bold bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
                        <Languages className="w-3 h-3 inline mr-1" />
                        {t.target_language}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(t.created_at)}
                      </span>
                      {t.bookmarked && (
                        <span className="text-xs font-bold bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full">
                          <Bookmark className="w-3 h-3 inline mr-1" />Bookmarked
                        </span>
                      )}
                    </div>
                    {t.main_object && (
                      <div className="mb-2">
                        <span className="text-xs font-bold text-pink-500 uppercase tracking-wider">Main: </span>
                        <span className="text-sm font-semibold text-slate-900 capitalize">{t.main_object}</span>
                        {t.main_object_translation && (
                          <span className="text-sm text-indigo-600 ml-2">→ {t.main_object_translation}</span>
                        )}
                      </div>
                    )}
                    {t.original_text && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-bold text-slate-400 mb-1">Original</p>
                          <p className="text-sm text-slate-700 line-clamp-2">{t.original_text}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-indigo-400 mb-1">Translated</p>
                          <p className="text-sm text-slate-900 font-medium line-clamp-2">{t.translated_text}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
